import type {
  ProfileType,
  SwitchSubPortfolioGroupCommandData,
  SwitchSubPortfolioGroupCommandDataVFrame,
} from '@festina/unitlink-general-types/unitlink'
import { type Optional, typeUtils } from '@festina-life/core'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import { useDataContext } from '@festina-life/ui/theme'
import { useState } from 'react'

import { useTranslateCover } from '../../../hooks/useTranslateCover'
import { useHook } from '../../../lenteHooks'
import type { CommandDataFormProps } from '../../commandActions/CommandDataFormAndSimulation'
import { LifeForm } from '../../forms/LifeForm'
import { LifeInput } from '../../inputs/LifeInput'
import { PartitionCommandSelectionTable } from './PartitionCommandSelectionTable'
import { mapUpdatePartitionCommandSelectionsToVFrame } from './utils'

const validateKey =
  typeUtils.createValidator<SwitchSubPortfolioGroupCommandData>()

export const SwitchSubPortfolioGroupForm = ({
  commandData,
  ...props
}: CommandDataFormProps<SwitchSubPortfolioGroupCommandData>) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const translateCover = useTranslateCover()
  const [profileType, setProfileType] = useState<ProfileType>()
  const { applyDate } = useDataContext()
  const commitmentDateProps = {
    currentOverrideCommitmentVariant: applyDate
      ? ('currentByCommitmentDate' as const)
      : undefined,
    commitmentDate: applyDate,
  }
  const toSubCommitmentName = useHook('pages.commitment.useSubCommitmentName')(
    commitmentDateProps,
  )
  const renderPolicy = useHook('pages.commitment.useLazyRenderPolicy')(
    commitmentDateProps,
  )
  const { tenantId, ownerId } = useHook('routing.general.useEntityParams')()
  const [switchCommandType, setSwitchCommandType] =
    useState<SwitchSubPortfolioGroupCommandData['switchCommandType']>()

  const [subPortfolioGroupKeys, setSubPortfolioGroupKeys] = useState<
    SwitchSubPortfolioGroupCommandData['subPortfolioGroupKeys']
  >([])

  const { data: mFrame } = useHook(
    'fetchers.unitlink.useFetchSwitchSubPortfolioGroupFrame',
  )(
    {
      ownerId,
      tenantId,
      requestBody: {
        _type: 'SwitchSubPortfolioGroupCommandData',
        switchCommandType,
        subPortfolioGroupKeys,
        partitionCommandSelections: profileType && [
          {
            _type: 'PartitionCommandSelection',
            profileType,
          },
        ],
        ...commandData,
      },
    },
    { enabled: props.shouldSkipFrame !== true },
  )

  const renderCoverName = (subPortfolioKey: Optional<string>) => {
    if (!subPortfolioKey) {
      return '-'
    }

    const [policyKey, coverKey] = subPortfolioKey.split('_')
    return `${renderPolicy(policyKey)} - ${translateCover(coverKey)}`
  }

  const initialValues: SwitchSubPortfolioGroupCommandData = {
    _type: 'SwitchSubPortfolioGroupCommandData',
  }

  const isSinglePartition =
    !mFrame?.addPartitionCommandSelections &&
    mFrame?.updatePartitionCommandSelections?.length === 1

  // Picking the properties from the mFrame that are relevant for the form
  const frame = props.shouldSkipFrame
    ? undefined
    : ({
        _type: 'SwitchSubPortfolioGroupCommandDataVFrame',
        switchCommandType: mFrame?.switchCommandType,
        subPortfolioGroupKeys: mFrame?.subPortfolioGroupKeys,
        resetSubPortfolioKeys: mFrame?.resetSubPortfolioKeys,
        partitionCommandSelections: mapUpdatePartitionCommandSelectionsToVFrame(
          mFrame?.updatePartitionCommandSelections,
        ),
      } satisfies SwitchSubPortfolioGroupCommandDataVFrame)

  return (
    <LifeForm initialValues={initialValues} frame={frame} {...props}>
      <>
        <LifeInput
          fallbackInputType="string"
          name={validateKey('switchCommandType')}
          label={translate('switchSubPortfolioGroupForm.switchCommandType')}
          renderFn={(value) =>
            translateEnum('unitLink.enum.SwitchCommandType', value)
          }
          onBlur={setSwitchCommandType}
        />
        <LifeInput
          fallbackInputType="array"
          name={validateKey('subPortfolioGroupKeys')}
          label={translate('switchSubPortfolioGroupForm.subPortfolioGroupKeys')}
          renderFn={(value) => toSubCommitmentName(value)}
          onBlur={setSubPortfolioGroupKeys}
        />
        {isSinglePartition && (
          <LifeInput
            fallbackInputType="string"
            name={validateKey('partitionCommandSelections.0.profileType')}
            label={translate('editPartitionCommandSelectionModal.profileType')}
            renderFn={(value) =>
              translateEnum('unitLink.enum.ProfileType', value)
            }
            onBlur={setProfileType}
          />
        )}

        <PartitionCommandSelectionTable
          mFrame={mFrame}
          isSinglePartition={isSinglePartition}
        />

        {frame?.resetSubPortfolioKeys && (
          <LifeInput
            fallbackInputType="array"
            name={validateKey('resetSubPortfolioKeys')}
            label={translate(
              'switchSubPortfolioGroupForm.resetSubPortfolioKeys',
            )}
            renderFn={(value: string) => renderCoverName(value)}
          />
        )}
      </>
    </LifeForm>
  )
}
